import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import {
  AuthStore,
  getCountryFlag,
  ServiceTicketCallType,
  ServiceTicketRoutingProvider,
  ServiceTicketStore,
  SUPPORTED_COUNTRY_CODE,
  SUPPORTED_LANGUAGE_DIAL_CODE,
  Helpers,
} from '../../../../../@store';
import { CommonStandaloneModule } from '../../../../common-standalone.module';
import {
  getFormattedPhoneNumber,
  getPhoneNumberWithoutDialCode,
  isPhoneNumberValid,
  PhoneValidators,
} from '../../../../pipes/format-phone-number/format-phone.helper';
import { FieldTextContentTooltipDirective } from '../../../../../../projects/ui/directives/tooltip/field-text-content-tooltip.directive';
import { isEqual } from 'lodash';
import { LetDirective } from '@ngrx/component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
  selector: 'app-service-ticket-call-type',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    MatSelectModule,
    MatFormFieldModule,
    FieldTextContentTooltipDirective,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatButtonModule,
    LetDirective,
  ],
  templateUrl: './service-ticket-call-type.component.html',
  styleUrls: ['./service-ticket-call-type.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceTicketCallTypeComponent implements OnInit, OnDestroy {
  ServiceTicketCallType = ServiceTicketCallType;
  ServiceTicketRoutingProvider = ServiceTicketRoutingProvider;
  selectedCallType = ServiceTicketCallType.CallNow;

  langCode: SUPPORTED_COUNTRY_CODE = SUPPORTED_COUNTRY_CODE.DE;
  public dialCodeValues = SUPPORTED_LANGUAGE_DIAL_CODE;
  public keys: string[] = Object.keys(this.dialCodeValues);

  phoneNumberWithoutDialCodeFormControl = new FormControl<string>('', [
    PhoneValidators.validatePhoneNumber(this.langCode),
  ]);
  errorMessage: string;
  isAllowedToUseCallNowByVkorg: boolean;
  routingProvider: string;
  isCallNowAvailable: boolean;

  public getCountryFlag = getCountryFlag;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(public serviceTicketStore: ServiceTicketStore, private authStore: AuthStore) {}

  ngOnInit(): void {
    this.authStore
      .getCountryCode()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((countryCode) => {
        if (SUPPORTED_COUNTRY_CODE[countryCode]) {
          this.langCode = SUPPORTED_COUNTRY_CODE[countryCode];
        }
      });

    this.phoneNumberWithoutDialCodeFormControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((phone) => {
        this.onPhoneNumberChange(phone);
      });

    this.authStore
      .getPhone()
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe((value: string) => {
        if (isPhoneNumberValid(value, this.langCode)) {
          const formattedPhone = getFormattedPhoneNumber(value, this.langCode);

          const phoneNumberWithoutDialCode = getPhoneNumberWithoutDialCode(
            formattedPhone,
            this.langCode
          );

          this.phoneNumberWithoutDialCodeFormControl.setValue(phoneNumberWithoutDialCode);
        }
      });

    this.serviceTicketStore.isAllowedToUseCallNowByVkorg$
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe((isAllowed) => {
        this.isAllowedToUseCallNowByVkorg = isAllowed;
        if (!isAllowed) {
          this.onToggle(ServiceTicketCallType.ArrangedCallback);
        }
      });

    combineLatest([this.authStore.isCompanyFromGermany$, this.serviceTicketStore.routingProvider$])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([isCompanyFromGermany, routingProvider]) => {
        this.routingProvider = routingProvider;
        if (isCompanyFromGermany) {
          this.setIsCallNowAvailable();
          this.selectedCallType =
            this.routingProvider === ServiceTicketRoutingProvider.Salesforce &&
            this.isCallNowAvailable
              ? ServiceTicketCallType.CallNow
              : ServiceTicketCallType.ArrangedCallback;
        }
      });
  }

  onPhoneNumberChange(phoneNumber: string): void {
    const phonePrefix = this.dialCodeValues[this.langCode];
    const fullPhoneNumber = `${phonePrefix}${phoneNumber}`;
    let isValidPhoneNumber = false;

    try {
      isValidPhoneNumber = this.phoneNumberWithoutDialCodeFormControl.valid;
    } catch (e) {}

    this.serviceTicketStore.updatePhoneNumber(fullPhoneNumber, isValidPhoneNumber);
  }

  onToggle(callType: ServiceTicketCallType): void {
    this.selectedCallType = callType;
    this.serviceTicketStore.updateCallType(callType);
  }

  onCallNowClicked(): void {
    this.serviceTicketStore.requestCallNow();
  }

  onArrangeCallbackClicked(): void {
    this.serviceTicketStore.requestArrangedCall();
  }

  onLangCodeChange(event: MatSelectChange): void {
    this.langCode = event.value;
  }

  getDialCodeValue(value: string): string {
    return this.dialCodeValues[value];
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private setIsCallNowAvailable(): void {
    const hourInGermany = dayjs().tz('Europe/Berlin').format('HH');
    const workingHoursWeek = { start: '07', end: '17' };
    const workingHoursWeekend = { start: '08', end: '16' };

    const day = dayjs().tz('Europe/Berlin').format('DD');
    const month = dayjs().tz('Europe/Berlin').format('MM');

    const dateToCompare = `${day}.${month}`;
    const holidays = ['01.01', '01.05', '03.10', '25.12', '26.12'];

    const isHolidayToday = holidays.includes(dateToCompare);

    if (isHolidayToday) {
      // workaround - treat all days like a holiday
      this.isCallNowAvailable = Helpers.isCallbackTimeWithinWorkingHours(hourInGermany, {
        workingHoursWeek: workingHoursWeekend,
        workingHoursWeekend,
      });
    } else {
      this.isCallNowAvailable = Helpers.isCallbackTimeWithinWorkingHours(hourInGermany, {
        workingHoursWeek,
        workingHoursWeekend,
      });
    }

    if (!this.isCallNowAvailable) {
      this.onToggle(ServiceTicketCallType.ArrangedCallback);
    }
  }
}
